import { Button } from '@lmc/ui-kit'
import { Heading, Text } from '@lmc/ui-kit/src/general'
import { Divider, InputNumber, Modal } from 'antd'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import type { ReconciliationRow } from '../../../Reports/types/reconciliation'

const modalDividerStyle = { margin: 0, padding: 0 } as const

type CustomAmountModalProps = {
  open: boolean
  row: ReconciliationRow | null
  onClose: () => void
  onSave: (row: ReconciliationRow, amount: number) => void
  isLoading?: boolean
}

export function CustomAmountModal({
  open,
  row,
  onClose,
  onSave,
  isLoading = false
}: CustomAmountModalProps) {
  const { t } = useTranslation()
  const [amount, setAmount] = useState<number | null>(null)

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (open) {
      const raw = row?.payAmount
      setAmount(raw != null ? parseFloat(raw) : null)
    }
  }, [open, row])

  const canSave = amount !== null && amount >= 0

  return (
    <StyledModal
      wrapProps={{ 'data-testid': 'custom-pay-amount-modal' }}
      open={open}
      onCancel={onClose}
      closable
      destroyOnClose
      width={580}
      title={
        <Stack $gap={24}>
          <ModalTitleHeading level={3}>
            {t('RECONCILIATION_CUSTOM_AMOUNT_TITLE')}
          </ModalTitleHeading>
          <Divider style={modalDividerStyle} />
        </Stack>
      }
      footer={
        <Stack $gap={16} $mt={24}>
          <RowEnd>
            <Button onClick={onClose} disabled={isLoading}>
              {t('CANCEL')}
            </Button>
            <Button
              variant="primary"
              onClick={() => row && canSave && onSave(row, amount!)}
              isLoading={isLoading}
              disabled={!canSave}
            >
              {t('SAVE')}
            </Button>
          </RowEnd>
        </Stack>
      }
    >
      <Stack $gap={24}>
        <ModalBody>
          <BodyText>{t('RECONCILIATION_CUSTOM_AMOUNT_BODY_1')}</BodyText>
          <BodyText>{t('RECONCILIATION_CUSTOM_AMOUNT_BODY_2')}</BodyText>
          <AmountInputWrapper>
            <StyledInputNumber
              data-testid="custom-pay-amount-input"
              value={amount}
              onChange={(val) => setAmount(val as number | null)}
              min={0}
              precision={2}
              controls={false}
              disabled={isLoading}
            />
            <CurrencySuffix>{t('RECONCILIATION_GBP_SUFFIX')}</CurrencySuffix>
          </AmountInputWrapper>
        </ModalBody>
        <Divider style={modalDividerStyle} />
      </Stack>
    </StyledModal>
  )
}

const StyledModal = styled(Modal)`
  .ant-modal-content {
    padding: 24px;
  }
  .ant-modal-header {
    padding: 0;
    margin: 0 0 12px;
    border-bottom: none;
  }
  .ant-modal-title {
    text-transform: none;
  }
  .ant-modal-body {
    padding: 0;
  }
  .ant-modal-footer {
    padding: 0;
    margin: 0;
    border-top: none;
  }
  .ant-modal-footer button:disabled {
    border: 1px solid transparent;
  }
`

const ModalTitleHeading = styled(Heading)`
  margin: 0;
  color: ${({ theme }) => theme.color.neutral.color3};
`

const RowEnd = styled.div`
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 8px;
`

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`

const BodyText = styled(Text).attrs({ level: 1 })`
  color: ${({ theme }) => theme.color.neutral.color4};
`

const AmountInputWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 0;
`

const StyledInputNumber = styled(InputNumber)`
  width: 160px;
  border-radius: 8px 0 0 8px;

  .ant-input-number-input {
    font-size: 16px;
  }
`

const CurrencySuffix = styled.div`
  display: flex;
  align-items: center;
  padding: 0 12px;
  height: 32px;
  background: ${({ theme }) => theme.color.neutral.color10};
  border: 1px solid ${({ theme }) => theme.color.neutral.color8};
  border-left: none;
  border-radius: 0 8px 8px 0;
  font-size: 14px;
  color: ${({ theme }) => theme.color.neutral.color4};
`

const Stack = styled.div<{ $gap?: number; $mt?: number }>`
  display: flex;
  flex-direction: column;
  gap: ${({ $gap = 8 }) => $gap}px;
  ${({ $mt }) => $mt != null && `margin-top: ${$mt}px;`}
  min-width: 0;
  width: 100%;
`
